import { MessageFile, SAMessageType } from "@missionlabs/smartagent-chat-components-lib/dist/ChatMessage/chat-message.types"
import {  MessageUserType } from '@missionlabs/smartagent-chat-components-lib'
import { ChatMessageEvent, IChatMessage } from "store/messages/messages.state"
import { ChatMessage } from '@missionlabs/smartagent-chat-components-lib';
import { useCallback, useMemo } from 'react';
import MetaChatMessage from "components/MetaChatMessage";
import useSendMessage from "hooks/useSendMessage";
import useApp from "hooks/useApp";
import { capitaliseString, isBanner, isStarRating } from 'utils';
import { DEFAULT_LANGUAGE } from "translations";
import { FocusOnMount } from "components/FocusOnMount";

const useDisplayName = () => {
    const { facia, company, systemDisplayName, language, appTextTranslations } = useApp();

    return useCallback(
        (message: IChatMessage) => {
            switch (message.role) {
                case 'CUSTOMER':
                    return appTextTranslations!.metaMessages![language ?? DEFAULT_LANGUAGE].you;
                case 'SYSTEM':
                    return systemDisplayName ?? capitaliseString(facia ?? company);
                case 'AGENT':
                    return message.displayName;
                default:
                    throw new Error('unrecognised participant role ' + message.role);
            }
        },
        [facia, company, systemDisplayName, language, appTextTranslations]
    );
}

const useRenderMessage = () => {
    const sendMessage = useSendMessage();
    const getDisplayName = useDisplayName();
    const { logoURL, customerIconURL, agentLogoURL, contactId, messagesReceipts, translation, ignoreTransformToLinkTexts, appTextTranslations, language } = useApp();
    // const messages = useMessages();

    const translations = useMemo(() => {
        return {
            typingAriaLabel: appTextTranslations?.typingAriaLabel?.[language] ?? appTextTranslations?.typingAriaLabel?.[DEFAULT_LANGUAGE]!,
            chatbotAlt: appTextTranslations?.chatbotAlt?.[language] ?? appTextTranslations?.chatbotAlt?.[DEFAULT_LANGUAGE]!,
            newMessageNotificationText: appTextTranslations?.newMessageNotificationText?.[language] ?? appTextTranslations?.newMessageNotificationText?.[DEFAULT_LANGUAGE]!,
            starRatingAriaLabel: appTextTranslations?.starRatingAriaLabel?.[language] ?? appTextTranslations?.starRatingAriaLabel?.[DEFAULT_LANGUAGE]!,
            noRatingAriaLabel: appTextTranslations?.noRatingAriaLabel?.[language] ?? appTextTranslations?.noRatingAriaLabel?.[DEFAULT_LANGUAGE]!,
            starAriaLabel: appTextTranslations?.starAriaLabel?.[language] ?? appTextTranslations?.starAriaLabel?.[DEFAULT_LANGUAGE]!,
            starsAriaLabel: appTextTranslations?.starsAriaLabel?.[language] ?? appTextTranslations?.starsAriaLabel?.[DEFAULT_LANGUAGE]!,
            quickRepliesAriaLabel: appTextTranslations?.quickRepliesAriaLabel?.[language] ?? appTextTranslations?.quickRepliesAriaLabel?.[DEFAULT_LANGUAGE]!,
            statusLabels: {
                read: appTextTranslations?.statusLabels?.read?.[language] ?? appTextTranslations?.statusLabels?.read?.[DEFAULT_LANGUAGE]!,
                delivered: appTextTranslations?.statusLabels?.delivered?.[language] ?? appTextTranslations?.statusLabels?.delivered?.[DEFAULT_LANGUAGE]!,
                sent: appTextTranslations?.statusLabels?.sent?.[language] ?? appTextTranslations?.statusLabels?.sent?.[DEFAULT_LANGUAGE]!,
            },
            readAtAriaLabel: appTextTranslations?.readAtAriaLabel?.[language] ?? appTextTranslations?.readAtAriaLabel?.[DEFAULT_LANGUAGE]!,
            hideText: appTextTranslations?.hideText?.[language] ?? appTextTranslations?.hideText?.[DEFAULT_LANGUAGE]!,
            seeText: appTextTranslations?.seeText?.[language] ?? appTextTranslations?.seeText?.[DEFAULT_LANGUAGE]!,
            messageText: appTextTranslations?.messageText?.[language] ?? appTextTranslations?.messageText?.[DEFAULT_LANGUAGE]!,
            downloadLabel: appTextTranslations?.downloadLabel?.[language] ?? appTextTranslations?.downloadLabel?.[DEFAULT_LANGUAGE]!,
            receivedAtText: appTextTranslations?.receivedAtText?.[language] ?? appTextTranslations?.receivedAtText?.[DEFAULT_LANGUAGE]!,
            sentAtText: appTextTranslations?.sentAtText?.[language] ?? appTextTranslations?.sentAtText?.[DEFAULT_LANGUAGE]!,
            says: appTextTranslations?.says?.[language] ?? appTextTranslations?.says?.[DEFAULT_LANGUAGE]!,
            getSelectXOptionsFromY: appTextTranslations?.getSelectXOptionsFromY?.[language] ?? appTextTranslations?.getSelectXOptionsFromY?.[DEFAULT_LANGUAGE]!,
            cancel: appTextTranslations?.cancel?.[language] ?? appTextTranslations?.cancel?.[DEFAULT_LANGUAGE]!,
            submit: appTextTranslations?.submit?.[language] ?? appTextTranslations?.submit?.[DEFAULT_LANGUAGE]!,
        }
    }, [language, appTextTranslations]);

    const updateContact = useCallback((attributes: Record<string, string>) => {
        return window.bchat.updateContact({
            attributes,
            contactId: contactId!
        })
    }, [contactId])

    const renderChatMessageConnect = useCallback(
        (message: IChatMessage) => {
            if (message.type === 'ATTACHMENT') {
                return (
                    <ChatMessage
                        key={message.id}
                        updateContact={updateContact}
                        sendMessage={sendMessage}
                        clientType={MessageUserType.CUSTOMER}
                        senderType={message.role as MessageUserType}
                        time={message.sentAt}
                        body={{
                            type: 'FILE_LINK',
                            src: (message.content as MessageFile)?.src,
                        }}
                        companyLogoUrl={
                            message.role === 'AGENT' && agentLogoURL ? agentLogoURL : logoURL
                        }
                        senderDisplayName={getDisplayName(message)}
                        isMarkdownEnabled={true}
                        translations={translations}
                    ></ChatMessage>
                );
            }
            switch (message.contentType) {
                case ChatMessageEvent.TEXT:
                case ChatMessageEvent.MARKDOWN:
                    return (
                      <ChatMessage
                        key={message.id}
                        updateContact={updateContact}
                        sendMessage={sendMessage}
                        clientType={MessageUserType.CUSTOMER}
                        senderType={message.role as MessageUserType}
                        time={message.sentAt}
                        body={{
                          type: "TEXT",
                          text: message.content as string,
                          translatedMessage: translation?.on ? message.originalMessage : "",
                        }}
                        companyLogoUrl={
                          message.role === "AGENT" && agentLogoURL
                            ? agentLogoURL
                            : logoURL
                        }
                        senderDisplayName={getDisplayName(message)}
                        isMarkdownEnabled={true}
                        receiptsActive={!!messagesReceipts?.enabled}
                        deliveredTimestamp={message.deliveredTimestamp}
                        readTimestamp={!!messagesReceipts?.disableReadReceipts ? "" : message.readTimestamp}
                        translations={translations}
                      />
                    );
                case ChatMessageEvent.PARTICIPANT_JOINED:
                case ChatMessageEvent.PARTICIPANT_LEFT:
                case ChatMessageEvent.CHAT_ENDED:
                case ChatMessageEvent.TRANSFER_SUCCESS:
                case ChatMessageEvent.TRANSFER_FAILURE:
                    return (
                        <ChatMessage
                            updateContact={updateContact}
                            sendMessage={sendMessage}
                            senderType={message.role as MessageUserType}
                            clientType={MessageUserType.CUSTOMER}
                            time={message.sentAt}
                            key={message.id}
                            userIconUrl={customerIconURL}
                            translations={translations}
                        >
                            <MetaChatMessage {...message} />
                        </ChatMessage>
                    );

                default:
                    return message.contentType;
            }
        },
        [getDisplayName, updateContact, sendMessage, customerIconURL, logoURL, agentLogoURL, messagesReceipts]
    );

    const renderChatMessageSAMapper = useCallback(
        (message: IChatMessage, lastMessage: boolean) => {
            const content = message.content as SAMessageType;
            if (
                (content.type === 'TEXT_QUICKREPLIES' ||
                    content.type === 'QUICK_REPLIES' ||
                    content.type === 'QUICK_REPLIES_MULTILINE') &&
                !lastMessage
            )
                content.showQuickRepliesOnMount = false;
            if (content.type === 'FORM' && !lastMessage) return null

            return (
                <ChatMessage
                    key={message.id}
                    clientType={MessageUserType.CUSTOMER}
                    time={message.sentAt}
                    senderType={message.role as MessageUserType}
                    body={content}
                    updateContact={updateContact}
                    sendMessage={sendMessage}
                    companyLogoUrl={logoURL}
                    senderDisplayName={getDisplayName(message)}
                    isMarkdownEnabled={message.contentType === ChatMessageEvent.MARKDOWN}
                    receiptsActive={!!messagesReceipts?.enabled}
                    deliveredTimestamp={message.deliveredTimestamp}
                    readTimestamp={!!messagesReceipts?.disableReadReceipts ? "" : message.readTimestamp}
                    ignoreTransformToLinkTexts={ignoreTransformToLinkTexts}
                    translations={translations}
            />
            );
        },
        [sendMessage, getDisplayName, updateContact, logoURL, messagesReceipts]
    );

    const wrapFocus = (render: Function) => 
        <FocusOnMount>{render()}</FocusOnMount>;

    const renderMessage = useCallback(
        (message: IChatMessage, lastMessage: boolean) => {
            if (isBanner(message)) return;

            const renderChatMessage = () => {
                if (typeof message.content === 'string' || !message.content) {
                    return renderChatMessageConnect(message);
                }
                return renderChatMessageSAMapper(message, lastMessage);
            }

            if (lastMessage && !isStarRating(message)) {
                return wrapFocus(renderChatMessage)
            }

            return renderChatMessage()
            
        },
        [renderChatMessageConnect, renderChatMessageSAMapper]
    );

    return renderMessage;

};

export default useRenderMessage;